/**
 * siliconeFacePipeline — a flattened ellipsoid suggesting a face
 * pushed up against a translucent silicone sheet. Bass dimples push
 * the surface outward from the viewer's side; the rendering emphasizes
 * subsurface scattering and chromatic dispersion through the skin.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SiliconeFacePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash13(vec3 p) {
  p = fract(p * 0.1031);
  p += dot(p, p.yzx + 33.33);
  return fract((p.x + p.y) * p.z);
}

float noise3(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float n000 = hash13(i);
  float n100 = hash13(i + vec3(1, 0, 0));
  float n010 = hash13(i + vec3(0, 1, 0));
  float n110 = hash13(i + vec3(1, 1, 0));
  float n001 = hash13(i + vec3(0, 0, 1));
  float n101 = hash13(i + vec3(1, 0, 1));
  float n011 = hash13(i + vec3(0, 1, 1));
  float n111 = hash13(i + vec3(1, 1, 1));
  return mix(
    mix(mix(n000, n100, f.x), mix(n010, n110, f.x), f.y),
    mix(mix(n001, n101, f.x), mix(n011, n111, f.x), f.y),
    f.z
  );
}

float fbm3(vec3 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise3(p);
    p = p * 2.05 + vec3(1.3, -2.7, 0.9);
    a *= 0.5;
  }
  return v;
}

float mapFace(vec3 p) {
  // Flattened ellipsoid: tall vertically, narrow horizontally.
  vec3 e = vec3(0.75, 1.05, 0.55);
  float d = length(p / e) - 1.0;
  // Bass-driven dimples from the viewer's side (+Z).
  float t = uTime;
  vec3 c1 = vec3(0.25 * cos(t * 0.3), 0.35 * sin(t * 0.27), 0.4);
  vec3 c2 = vec3(0.25 * cos(t * 0.39 + 2.0), -0.2 * sin(t * 0.31), 0.4);
  vec3 c3 = vec3(-0.2 * cos(t * 0.45 - 1.1), 0.1 * sin(t * 0.43), 0.4);
  d -= uBass.x * exp(-dot(p - c1, p - c1) * 6.0) * 0.32;
  d -= uBass.y * exp(-dot(p - c2, p - c2) * 6.0) * 0.32;
  d -= uBass.z * exp(-dot(p - c3, p - c3) * 6.0) * 0.32;
  // Mid-band vertical smile/frown.
  d += sin(p.y * 6.0 + t * 1.2) * 0.02 * (0.5 + uMid.y);
  // Skin pore field.
  d += (fbm3(p * 20.0 + vec3(0.0, t * 0.4, 0.0)) - 0.5) * 0.010;
  return d;
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.0015, 0.0);
  return normalize(vec3(
    mapFace(p + e.xyy) - mapFace(p - e.xyy),
    mapFace(p + e.yxy) - mapFace(p - e.yxy),
    mapFace(p + e.yyx) - mapFace(p - e.yyx)
  ));
}

// Sub-surface scattering — a quick reverse-ray estimate through the skin.
float sss(vec3 p, vec3 n) {
  vec3 q = p + n * 0.18;
  float v = 0.0;
  for (int i = 0; i < 5; i++) {
    v += fbm3(q * (3.5 + float(i) * 0.4) + vec3(uTime * 0.3)) * (0.5 - float(i) * 0.08);
  }
  return clamp(v * 1.5, 0.0, 1.0);
}

void main() {
  vec2 uv = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  uv.x *= uResolution.x / uResolution.y;
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.2, 0.0, 1.0), 0.65);
  mid = pow(clamp(mid * 2.9, 0.0, 1.0), 0.75);
  high = pow(clamp(high * 3.5, 0.0, 1.0), 0.85);

  vec3 ro = vec3(0.0, 0.0, 2.4);
  vec3 rd = normalize(vec3(uv, -1.6));

  // Three chromatic passes.
  vec3 col = vec3(0.0);
  float dispersion = 0.006 + mid * 0.022;
  for (int ch = 0; ch < 3; ch++) {
    float fc = float(ch) - 1.0;
    vec3 rdc = normalize(vec3(uv * (1.0 + fc * dispersion), -1.6));
    float t = 0.0;
    bool hit = false;
    vec3 p = ro;
    for (int i = 0; i < 90; i++) {
      float d = mapFace(p);
      if (d < 0.001) { hit = true; break; }
      if (t > 7.0) break;
      t += d * 0.85;
      p = ro + rdc * t;
    }
    vec3 c = vec3(0.95, 0.78, 0.72); // warm skin tone in shadow
    if (hit) {
      vec3 n = calcNormal(p);
      vec3 ldir = normalize(vec3(0.3, 0.5, 0.8));
      float diff = clamp(dot(n, ldir), 0.0, 1.0);
      float fres = pow(1.0 - clamp(dot(n, -rdc), 0.0, 1.0), 3.0);
      vec3 h = normalize(ldir - rdc);
      float spec = pow(clamp(dot(n, h), 0.0, 1.0), 32.0);
      float sub = sss(p, n);
      // Subsurface light tinted with bass.
      vec3 sssCol = mix(
        vec3(1.0, 0.6, 0.55),
        vec3(0.95, 0.7, 0.85),
        0.5 + 0.5 * sin(uTime * 0.5 + length(p) * 2.0)
      );
      c = vec3(0.0);
      c += diff * vec3(0.96, 0.82, 0.75);
      c += fres * vec3(0.95, 0.85, 0.95) * (0.5 + high * 1.2);
      c += sssCol * sub * (0.5 + bass * 0.7);
      c += spec * vec3(1.0) * (0.3 + bass * 0.6);
      // Pore texture shadow.
      float pores = fbm3(p * 60.0);
      c *= 0.85 + 0.15 * pores;
    }
    col[ch] = c[ch];
  }

  float vig = pow(16.0 * vUV.x * (1.0 - vUV.x) * vUV.y * (1.0 - vUV.y), 0.25);
  col *= vig;
  col = col / (1.0 + col);
  col = pow(col, vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}`;

export function computeSiliconeFaceBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.95);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.8));
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.5);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.15 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.75 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.5 + 3.1);
  out[6] = 0.3 + 0.5 * Math.sin(t * 4.0 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 6.0 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 8.0 + 2.1);
}

export function computeSiliconeFaceRealtimeBands(freqData: Uint8Array, out: Float32Array): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.008], [0.008, 0.030], [0.030, 0.060],
    [0.060, 0.150], [0.150, 0.260], [0.260, 0.420],
    [0.420, 0.620], [0.620, 0.800], [0.800, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    let s = 0, c = 0;
    const a = Math.floor(lo * N);
    const b = Math.floor(hi * N);
    for (let k = a; k < b; k++) { s += freqData[k]; c++; }
    out[i] = c > 0 ? (s / c) / 255 : 0;
  }
}

export function createSiliconeFacePipeline(gl: WebGL2RenderingContext): SiliconeFacePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawSiliconeFace(
  gl: WebGL2RenderingContext,
  pipe: SiliconeFacePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}